import React, { useState } from 'react';
import { Search, CheckCircle2, XCircle, Loader2, Award, Calendar, Hash, User } from 'lucide-react';
import { Sec, SH, FormField } from '../components/Shared';
import { CERTIFICATE_VERIFY_URL } from '../data/websiteData';

const CertificatePage = () => {
  const [certId, setCertId] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, valid, invalid
  const [result, setResult] = useState(null);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!certId.trim()) return;

    setStatus('loading');

    try {
      const res = await fetch(`${CERTIFICATE_VERIFY_URL}?id=${encodeURIComponent(certId)}`);
      const data = await res.json();

      if (data.valid) {
        setResult(data);
        setStatus('valid');
      } else {
        setStatus('invalid');
      }
    } catch (error) {
      console.error(error);
      setStatus('invalid');
    }
  };

  return (
    <div className="af">
      <Sec py={160} bg="var(--grad-hero)">
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <SH eyebrow="Official Records" title="Certificate Verification (Beta)" sub="Verify the authenticity of any official ICD Information Technology Club certificate." />

          <div className="card form-card">
            <form onSubmit={handleVerify} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'start', marginBottom: status !== 'idle' ? 40 : 0 }} className="show-m-stack">
              <div style={{ flex: 1 }}>
                <FormField
                  id="certId"
                  label="Certificate ID"
                  placeholder="ICDITC-XXXX"
                  value={certId}
                  onChange={(id, val) => setCertId(val)}
                  icon={<Search size={16} />}
                />
              </div>
              <button className="btn btn-primary btn-lg" type="submit" disabled={status === 'loading' || !certId.trim()} style={{ height: 50, marginTop: 26 }}>
                {status === 'loading' ? <Loader2 size={18} className="spin" /> : 'Verify'}
              </button>
            </form>

            {status === 'valid' && result && (
              <div className="af" style={{ background: 'var(--surface-container-low)', borderRadius: 12, padding: 32, border: '1px solid var(--green)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--green-light)', color: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircle2 size={24} />
                  </div>
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--on-surface)' }}>Certificate is Valid</h3>
                    <div style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>Officially verified by ICDITC</div>
                  </div>
                </div>

                <div className="g2" style={{ gap: 20 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <User size={16} style={{ color: 'var(--slate-400)', marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Recipient Name</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on-surface)' }}>{result.name}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <Award size={16} style={{ color: 'var(--slate-400)', marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Event / Workshop</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on-surface)' }}>{result.event}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <Calendar size={16} style={{ color: 'var(--slate-400)', marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Issue Date</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on-surface)' }}>{result.date}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <Hash size={16} style={{ color: 'var(--slate-400)', marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Certificate ID</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--on-surface)' }}>{result.id}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {status === 'invalid' && (
              <div className="af" style={{ background: 'rgba(239, 68, 68, 0.05)', borderRadius: 12, padding: 32, border: '1px solid rgba(239, 68, 68, 0.2)', textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <XCircle size={28} />
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 8 }}>Certificate Not Found</h3>
                <p style={{ fontSize: 14, color: 'var(--on-surface-variant)' }}>We couldn't find a record for the Certificate ID you entered. Please check the ID and try again.</p>
              </div>
            )}
          </div>

          <div style={{ marginTop: 40, textAlign: 'center', fontSize: 14, color: 'var(--slate-500)', lineHeight: 1.6 }}>
            <p><strong>Note:</strong> Only certificates issued after January 2024 are available in the digital verification system. For older certificates, please contact info@icditc.club</p>
          </div>
        </div>
      </Sec>
    </div>
  );
};

export default CertificatePage;
